import { ChatClient } from './chat-client';
import { type ChatConnectionStatus } from './chat-connection-status';

/** Chat manager configuration. */
export type ChatManagerConfiguration = Readonly<{

  /** Token factory function. */
  tokenFactory: () => Promise<string>;

  /** Callback function to be called on shutdown. */
  onShutdown?: () => void;

  /** Callback function to be called on error. */
  onError?: (error: string) => void;

  /** Callback function to be called on successful initialization. */
  onInitialized?: (chatClient: ChatClient) => void;

  /** Callback function to be called on connection status change. */
  onConnectionStatusChanged?: (status: ChatConnectionStatus) => void;
}>;

/**
 * Chat session manager.
 *
 * It's framework agnostic orchestrator
 * and aggregates full-cycle chat client management workflow including initialization and destruction.
 * Actually, we can use {@link ChatClient} directly,
 * but this class simplifies the workflow and can be extended with additional logic if needed.
 */
export class ChatSessionManager {
  private readonly config: ChatManagerConfiguration;

  private isInitializing = false;

  private client: ChatClient | null = null;

  public constructor(config: ChatManagerConfiguration) {
    this.config = config;
  }

  /** Initialize chat manager. */
  public async initialize(): Promise<void> {
    if (this.isInitializing) {
      return;
    }

    try {
      this.isInitializing = true;

      if (this.client != null) {
        await this.destroy();
      }
      const token = await this.config.tokenFactory();
      const newClient = new ChatClient(token);
      this.client = newClient;

      this.client.onInitFailed(async () => {
        await this.destroy();
        this.config.onError?.('Client initialization failed.');
      });
      this.client.onInitialized(() => {
        this.config.onInitialized?.(newClient);
        this.setupInternalListeners();
      });
    } catch {
      this.config.onError?.('Client initialization failed.');
    }
  }

  /** Destroy chat manager. */
  public async destroy(): Promise<void> {
    await this.client?.shutdown();
    this.client = null;
    this.config.onShutdown?.();
  }

  private setupInternalListeners(): void {
    this.client?.onTokenAboutToExpire(async () => {
      try {
        const newToken = await this.config.tokenFactory();
        await this.client?.updateToken(newToken);
      } catch {
        this.config.onError?.('Failed to update token.');
      }
    });

    this.client?.onTokenExpired(() => this.initialize());

    this.client?.onConnectionStateChanged(connectionState => {
      switch (connectionState) {
        case 'connected':
          this.config.onConnectionStatusChanged?.('connected');
          break;
        case 'connecting':
        case 'retrying':
          this.config.onConnectionStatusChanged?.('connecting');
          break;
        case 'disconnecting':
        case 'disconnected':
          this.config.onConnectionStatusChanged?.('disconnected');
          break;
        case 'denied':
        case 'error':
        case 'unknown':
          this.config.onConnectionStatusChanged?.('error');
          break;
        default:
          break;
      }
    });
  }
}
